import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBondWheelGame, selectBondWheelLastSettledGame } from "../redux/bondWheelSlice";
import { getGameWinningSliceIndices } from "../utils/bondWheelHelpers";

const LIVE_GAME_STATUSES = ["Active", "Locked"];
const SPIN_RESULT_DELAY_MS = 2400;
const SPIN_POLL_MS = 3000;

const isTerminalGame = (status) => status === "Settled" || status === "Cancelled";

export function useBondWheelLiveSpin(game, selectedGameId) {
  const dispatch = useDispatch();
  const lastSettled = useSelector(selectBondWheelLastSettledGame);
  const [isWheelSpinning, setIsWheelSpinning] = useState(false);
  const [winningSliceIndex, setWinningSliceIndex] = useState(null);
  const [winningSliceIndices, setWinningSliceIndices] = useState([]);
  const lastHandledSettleRef = useRef(null);
  const settlePresentationRef = useRef(null);
  const settleDelayRef = useRef(null);

  const applySliceIndices = useCallback((source) => {
    const indices = getGameWinningSliceIndices(source);
    if (indices.length > 0) {
      setWinningSliceIndices(indices);
      setWinningSliceIndex(indices[0]);
    }
  }, []);

  const presentSettleResult = useCallback((source) => {
    if (!source?._id) return;

    const indices = getGameWinningSliceIndices(source);
    const presentationKey = `${source._id}-${source.status}-${indices.join(",") || (source.winningSliceIndex ?? "x")}`;
    if (settlePresentationRef.current === presentationKey) return;
    settlePresentationRef.current = presentationKey;

    if (source.status === "Settled" && indices.length > 0) {
      applySliceIndices(source);
    }

    setIsWheelSpinning(true);
    if (settleDelayRef.current) clearTimeout(settleDelayRef.current);

    settleDelayRef.current = setTimeout(() => {
      setIsWheelSpinning(false);
    }, SPIN_RESULT_DELAY_MS);
  }, [applySliceIndices]);

  useEffect(() => {
    setIsWheelSpinning(false);
    setWinningSliceIndex(null);
    setWinningSliceIndices([]);
    settlePresentationRef.current = null;
  }, [selectedGameId]);

  useEffect(() => {
    if (!game) {
      setWinningSliceIndex(null);
      setWinningSliceIndices([]);
      setIsWheelSpinning(false);
      return;
    }

    const indices = getGameWinningSliceIndices(game);

    if (game.status === "Settled" && indices.length > 0) {
      setWinningSliceIndices(indices);
      setWinningSliceIndex(indices[0]);
      if (isWheelSpinning) presentSettleResult(game);
      return;
    }

    if (game.status === "Cancelled" && isWheelSpinning) {
      presentSettleResult(game);
      return;
    }

    if (game.status !== "Settled" && game.status !== "Cancelled") {
      setWinningSliceIndex(null);
      setWinningSliceIndices([]);
    }
  }, [game?._id, game?.status, game?.winningSliceIndex, game?.winningSliceIndices, isWheelSpinning, presentSettleResult]);

  useEffect(() => {
    if (!game || !selectedGameId || isTerminalGame(game.status)) return undefined;
    if (!LIVE_GAME_STATUSES.includes(game.status)) return undefined;

    const deadlineMs = new Date(game.deadline).getTime();
    if (!Number.isFinite(deadlineMs)) return undefined;

    const msUntilDeadline = deadlineMs - Date.now();

    if (msUntilDeadline <= 0) {
      setIsWheelSpinning(true);
      dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
      const interval = setInterval(() => {
        dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
      }, SPIN_POLL_MS);
      return () => clearInterval(interval);
    }

    const timer = setTimeout(() => {
      setIsWheelSpinning(true);
      dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
    }, msUntilDeadline);

    return () => clearTimeout(timer);
  }, [dispatch, game?._id, game?.deadline, game?.status, selectedGameId]);

  useEffect(() => {
    if (!isWheelSpinning || !selectedGameId) return undefined;
    const interval = setInterval(() => {
      dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
    }, SPIN_POLL_MS);
    return () => clearInterval(interval);
  }, [dispatch, isWheelSpinning, selectedGameId]);

  useEffect(
    () => () => {
      if (settleDelayRef.current) clearTimeout(settleDelayRef.current);
    },
    []
  );

  useEffect(() => {
    if (!lastSettled || !selectedGameId) return;
    if (String(lastSettled._id) !== String(selectedGameId)) return;
    if (!isWheelSpinning) return;
    if (lastHandledSettleRef.current === String(lastSettled._id) + lastSettled.status) return;
    lastHandledSettleRef.current = String(lastSettled._id) + lastSettled.status;

    if (lastSettled.status === "Settled") {
      applySliceIndices(lastSettled);
      presentSettleResult(lastSettled);
    } else if (lastSettled.status === "Cancelled") {
      presentSettleResult(lastSettled);
    }
  }, [lastSettled, selectedGameId, isWheelSpinning, presentSettleResult, applySliceIndices]);

  return { isWheelSpinning, winningSliceIndex, winningSliceIndices };
}
